import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  KeyboardEvent,
  FocusEventHandler,
  useMemo,
} from 'react';

import { UseDropdownProps, useDropdown } from '@adsk/alloy-react-dropdown';
import { useOnClickOutside } from '@adsk/alloy-react-helpers';
import { getInteractiveStyles } from '@adsk/alloy-react-theme';

import useIs12Hour from './useIs12Hour';
import useSelectableTimes from './useSelectableItems';
import {
  HOURS_ID,
  InitialTime,
  SelectTime,
  TIME_PERIOD_ID,
  TimePickerTime,
} from '../consts';
import { isPropControlled } from '../helpers';

type UseTimePickerProps = {
  disabled?: boolean;
  is12Hour?: boolean;
  selectIncrement?: number;
  inlineEdit?: boolean;
  initialTime?: InitialTime;
  onChange?: (value?: TimePickerTime | undefined) => void;
  onFocus?: FocusEventHandler<HTMLDivElement>;
  onBlur?: FocusEventHandler<HTMLDivElement>;
  onEditDone?: (value?: TimePickerTime | undefined) => void;
  value?: InitialTime;
};

const DEFAULT_TIME = { hours: 0, minutes: 0 };

export default ({
  disabled,
  selectIncrement,
  inlineEdit,
  initialTime,
  onChange,
  onFocus: onFocusProp,
  onBlur: onBlurProp,
  is12Hour: is12HourOverride,
  onEditDone,
  value,
}: UseTimePickerProps) => {
  const [selectedTime, setSelectedTime] = useState<InitialTime>(
    value || initialTime || DEFAULT_TIME,
  );
  const [forceRemount, setForceRemount] = useState(1);
  const [edit, setEdit] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const interactiveStyles = getInteractiveStyles(!!disabled);
  const is12Hour = useIs12Hour({ is12HourOverride });
  const selectableTimes = useSelectableTimes({
    increment: selectIncrement,
    is12Hour,
  });
  const [fireOnEditDone, setFireOnEditDone] = useState(false);
  const [menuHasChangedTime, setMenuHasChangedTime] = useState(false);
  const isControlled = isPropControlled(value);

  useEffect(() => {
    if (isControlled && value && menuHasChangedTime) {
      setSelectedTime(value);
      setForceRemount((forceRemount + 1) % 10);
    }
  }, [isControlled, value, forceRemount, menuHasChangedTime]);

  const controlOnChange = useCallback(
    (time: TimePickerTime | undefined) => {
      const matchingTime = selectableTimes.find(
        (x) =>
          x.value.hours === time?.hours && x.value.minutes === time?.minutes,
      );

      if (!isControlled) {
        setSelectedTime((prevTime) => ({
          hours: time?.hours ?? prevTime?.hours,
          minutes: time?.minutes ?? prevTime?.minutes,
        }));
      }

      onChange?.({
        ...(matchingTime?.value || time),
        is24Hour: !is12Hour,
      });
    },
    [is12Hour, onChange, selectableTimes, isControlled],
  );

  const menuOnChange = useCallback(
    (selectedItem: SelectTime | null | undefined) => {
      // onEditDone needs to be called once selectedTime state variable is set
      setFireOnEditDone(true);
      if (!isControlled) {
        setSelectedTime(selectedItem?.value || DEFAULT_TIME);
        setForceRemount((forceRemount + 1) % 10);
      } else {
        setMenuHasChangedTime(true);
      }

      onChange?.({
        ...selectedItem?.value,
        is24Hour: !is12Hour,
      });
    },
    [forceRemount, is12Hour, onChange, isControlled],
  );

  const handleEdit = useCallback(() => {
    if (inlineEdit && !disabled && !edit) {
      setEdit(true);
    }
  }, [disabled, edit, inlineEdit]);

  useEffect(() => {
    if (fireOnEditDone) {
      setFireOnEditDone(false);
      onEditDone?.({
        ...(isControlled ? value : selectedTime),
        is24Hour: !is12Hour,
      });
      setMenuHasChangedTime(false);
    }
  }, [fireOnEditDone, selectedTime, is12Hour, onEditDone, value, isControlled]);

  const selectedItem = useMemo(() => {
    let newSelectedTime = selectedTime;

    if (isControlled && value) {
      newSelectedTime = value;
    }

    return (
      selectableTimes.find(
        (x) =>
          x.value.hours === newSelectedTime?.hours &&
          x.value.minutes === newSelectedTime?.minutes,
      ) || null
    );
  }, [selectableTimes, selectedTime, value, isControlled]);

  // This fixes an issue where if a menu item is highlighted a blur action would
  // cause the item to be selected, documented here: https://github.com/downshift-js/downshift/issues/1010
  // it is fixed in useComboBox but not in useSelect
  const customStateReducer = useCallback<
    NonNullable<UseDropdownProps<SelectTime>['stateReducer']>
  >(
    (state, actionAndChanges) => {
      const { type } = actionAndChanges;
      let { changes } = actionAndChanges;

      switch (type) {
        case useDropdown.stateChangeTypes.ToggleButtonBlur:
          if (changes.selectedItem === undefined) {
            return changes;
          }

          const { selectedItem, ...changesWithoutItem } = changes;

          // if the highlighted menu item is the same as the selected time, then don't mess
          // with selectedItem so the auto selection still works
          if (
            selectedItem?.value.hours !== selectedTime.hours ||
            selectedItem?.value.minutes !== selectedTime.minutes
          ) {
            // setting selectedItem to null avoids a strange issue where menu change events
            // are not fired for the next selection made
            changes = { ...changesWithoutItem, selectedItem: null };
          }

          if (containerRef.current?.contains(document.activeElement)) {
            return { ...changes, isOpen: true };
          } else {
            return changes;
          }
        default:
          return changes;
      }
    },
    [selectedTime.hours, selectedTime.minutes],
  );

  const {
    controlProps: {
      getToggleButtonProps: rawGetToggleButtonProps,
      ...controlProps
    },
    menuProps,
    setHighlightedIndex,
    closeMenu,
  } = useDropdown({
    items: selectableTimes,
    selectedItem,
    onSelectedItemChange: (changes) => {
      menuOnChange(changes?.selectedItem);
    },
    stateReducer: customStateReducer,
  });

  useEffect(() => {
    if (menuProps.isOpen) {
      if (!selectedItem) {
        const index =
          selectableTimes.findIndex(
            (x) =>
              x.value.hours > selectedTime.hours ||
              (x.value.hours === selectedTime.hours &&
                x.value.minutes > selectedTime.minutes),
          ) - 1;

        setHighlightedIndex(index >= 0 ? index : 0);
      } else {
        setHighlightedIndex(selectableTimes.indexOf(selectedItem));
      }
    }
  }, [
    selectableTimes,
    selectedItem,
    selectedTime,
    setHighlightedIndex,
    menuProps.isOpen,
  ]);

  const onClickOutside = React.useCallback(() => setEdit(false), []);
  useOnClickOutside(edit, onClickOutside, containerRef);

  // If the target and related target elements are both within the time picker, return true;
  const isEventStillWithinTimePicker = useCallback(
    (e: React.FocusEvent) =>
      e.target.parentElement?.contains(e.relatedTarget) ||
      e.relatedTarget?.parentElement?.contains(e.target),
    [],
  );

  const onFocus: FocusEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (disabled) {
        return;
      }

      if (isEventStillWithinTimePicker(e)) {
        return;
      }

      handleEdit();

      onFocusProp?.(e);
    },
    [disabled, handleEdit, isEventStillWithinTimePicker, onFocusProp],
  );

  const onBlur: FocusEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (disabled) {
        return;
      }

      if (isEventStillWithinTimePicker(e)) {
        return;
      }

      onBlurProp?.(e);

      // user has entered time manually and menu control has lost focus so
      // close the dropdown menu if open
      if (menuProps.isOpen) {
        closeMenu();
      }
    },
    [
      closeMenu,
      disabled,
      isEventStillWithinTimePicker,
      menuProps.isOpen,
      onBlurProp,
    ],
  );

  const toggleButtonOnKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'ArrowRight') {
        document.getElementById(HOURS_ID)?.focus();
      } else if (e.key === 'ArrowLeft') {
        document.getElementById(TIME_PERIOD_ID)?.focus();
      }
    },
    [],
  );

  const getToggleButtonProps = useCallback<typeof rawGetToggleButtonProps>(
    (x) => rawGetToggleButtonProps({ ...x, onKeyDown: toggleButtonOnKeyDown }),

    [rawGetToggleButtonProps, toggleButtonOnKeyDown],
  );

  return {
    forceRemount,
    edit,
    selectedTime,
    containerRef,
    interactiveStyles,
    controlProps: {
      ...controlProps,
      onFocus,
      onBlur,
      getToggleButtonProps,
    },
    menuProps,
    controlOnChange,
  };
};
